/**
 * 69.10 step 4, the lighter as a body (the owner: "all the way down but I think very little range options can actually hit
 * it currently"; "sure frames as loot"): the lighter's hit shape and parts table in the ship frame convention of
 * core/shipBody.ts (people; +z forward, y up from the belly, x to its left) — the lifting body A (67.75: 42 m, a squat hull
 * flat underneath, two canted fins, four lift fans in the belly, three bells aft, the rear ramp): the **hull** (one box, the
 * body's own health), the **fins** (unmarked), the **fans** (four boxes under the belly: `function`, a tenth each, detaching —
 * two gone, it is lost as a dropship is with two nacelles gone), the **bells** (three aft: `function`, 0.15 each — the look's;
 * a bell gone does not lose it), the **ramp** (`cover`, a tenth, detaching). Its shape rides `lighterPose` through
 * `shipShapes` under `LIGHTER_ID_BASE + the run's id` (the ship shapes' id space: flights from 0, parked ships below, lighters
 * from a million), so rounds stop at it, sight breaks on it, blasts and flak wear it, and the bots' rockets and the point
 * defence take it as a ship, all the way down from the entry. Lost, it crashes (core/shipCrash.ts) with its frames as loot.
 */
import type { HitPart } from '../types/body';
import type { MarkedPart } from '../constants/damage';
import type { ShipFrame } from './shipBody';
import type { LighterPose } from './lighter';
import { LIGHTER } from '../constants/action';
import { dcos, dsin } from './dmath';
import { vecAdd, vecScale, vecNormalize, vecSub, vecDot } from '../math';
import { rightOf } from './surfaceWalk';

/** Lighter runs' ids in the ship shapes (a run's id plus this). */
export const LIGHTER_ID_BASE = 1_000_000;
export const isLighterId = (id: number): boolean => id >= LIGHTER_ID_BASE;

/** The lighter's parts (people): 0 the hull, 1–2 the fins, 3–6 the fans (front left, front right, rear left, rear right), 7–9 the bells, 10 the ramp. */
export const LIGHTER_BODY: readonly HitPart[] = [
  { shape: 'box', centre: [0, 2.0, 0], half: [3.0, 2.0, 11.5] },
  { shape: 'box', centre: [2.6, 3.6, -7.0], half: [0.3, 1.4, 2.0] },
  { shape: 'box', centre: [-2.6, 3.6, -7.0], half: [0.3, 1.4, 2.0] },
  { shape: 'box', centre: [2.0, 0.5, 5.0], half: [0.9, 0.5, 0.9] },
  { shape: 'box', centre: [-2.0, 0.5, 5.0], half: [0.9, 0.5, 0.9] },
  { shape: 'box', centre: [2.0, 0.5, -5.0], half: [0.9, 0.5, 0.9] },
  { shape: 'box', centre: [-2.0, 0.5, -5.0], half: [0.9, 0.5, 0.9] },
  { shape: 'box', centre: [1.8, 1.8, -12.0], half: [0.7, 0.7, 0.8] },
  { shape: 'box', centre: [0, 1.8, -12.0], half: [0.7, 0.7, 0.8] },
  { shape: 'box', centre: [-1.8, 1.8, -12.0], half: [0.7, 0.7, 0.8] },
  { shape: 'box', centre: [0, 0.8, -11.6], half: [1.8, 0.3, 1.0] },
];
export const LIGHTER_FANS: readonly number[] = [3, 4, 5, 6];
export const LIGHTER_BELLS: readonly number[] = [7, 8, 9];
export const LIGHTER_RAMP = 10;
/** The marked parts, in LighterRun.parts' order. */
export const LIGHTER_PARTS: readonly MarkedPart[] = [
  { part: 3, role: 'function', share: 0.1, detaches: true }, { part: 4, role: 'function', share: 0.1, detaches: true },
  { part: 5, role: 'function', share: 0.1, detaches: true }, { part: 6, role: 'function', share: 0.1, detaches: true },
  { part: 7, role: 'function', share: 0.15 }, { part: 8, role: 'function', share: 0.15 }, { part: 9, role: 'function', share: 0.15 },
  { part: 10, role: 'cover', share: 0.1, detaches: true },
];
/** The parts' whole healths (the shares of LIGHTER.health). */
export const lighterWholeParts = (): number[] => LIGHTER_PARTS.map(m => m.share * LIGHTER.health);

/** A posed lighter's frame: the belly's point, up the surface's, the nose pitched by the pose's pitch about its left (nose up positive). */
export function lighterFrame(pose: Pick<LighterPose, 'pos' | 'dir' | 'heading' | 'pitch'>): ShipFrame {
  const up0 = vecNormalize(pose.dir), front0 = vecNormalize(vecSub(pose.heading, vecScale(up0, vecDot(pose.heading, up0))));
  const left = vecScale(rightOf(front0, up0), -1);
  const c = dcos(pose.pitch), s = dsin(pose.pitch);
  const front = vecAdd(vecScale(front0, c), vecScale(up0, s)), up = vecAdd(vecScale(up0, c), vecScale(front0, -s));
  return { origin: pose.pos, up, front, left, tilt: 0 };
}
